/**
 * Sessions API + TanStack Query hooks.
 *
 * Drag-to-reschedule
 * ------------------
 * The optimistic update + rollback are owned by the ``useUpdateSession``
 * mutation (Phase-4 review fix): every caller (CalendarPage drop handler,
 * SessionDetailDrawer, …) gets the same behaviour automatically. The
 * caller passes the currently-displayed list params so we can patch the
 * exact React Query cache key the user is looking at.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@core/api/client';
import type { components } from '@api/index';

/**
 * 11E.7 fields are declared by hand rather than pulled from
 * `components['schemas']`.
 *
 * `shared/api-types/index.ts` is stale against the current backend — a full
 * regeneration today drops `PaginatedResult_{Package,Staff,Student,Subject}
 * Response_`, which catalog/packages/people still import, so regenerating
 * is a cross-cutting change owned by PRE-REQ-C, not by this feature. Same
 * reasoning and same workaround as `@features/attendance/api/leaves.ts`.
 *
 * When PRE-REQ-C lands, delete these hand-declarations and take the
 * generated types.
 */
export type SessionResponse = components['schemas']['SessionResponse'] & {
  /** Denormalised from the parent class; null on pre-11E.7 sessions. */
  capacity?: number | null;
  /** Derived server-side from `student_ids`. */
  booked_count?: number;
  /** Set by the Phase 5 attendance service on first successful mark. */
  attendance_marked_at?: string | null;
};
export type SessionUpdate = components['schemas']['SessionUpdate'];
export type SessionCancelRequest = components['schemas']['SessionCancelRequest'];

export interface SessionDuplicateRequest {
  starts_at: string;
  ends_at: string;
  override_conflicts?: boolean;
}

export type SeriesEditScope = 'following' | 'all';

export interface SeriesEditRequest {
  scope: SeriesEditScope;
  recurrence?: components['schemas']['Recurrence'] | null;
  teacher_id?: string | null;
  room_id?: string | null;
  branch_id?: string | null;
  capacity?: number | null;
  override_conflicts?: boolean;
}

export interface SeriesEditResult {
  class_id: string;
  original_class_id: string;
  split: boolean;
  sessions_deleted: number;
  sessions_created: number;
  sessions_preserved: number;
}

export interface ListSessionsParams {
  from: string; // ISO datetime
  to: string;
  teacher_id?: string;
  room_id?: string;
  student_id?: string;
  status?: string;
  subject_id?: string;
  branch_id?: string;
}

export const sessionKeys = {
  all: ['calendar', 'sessions'] as const,
  list: (p: ListSessionsParams) => ['calendar', 'sessions', 'list', p] as const,
  detail: (id: string) => ['calendar', 'sessions', 'detail', id] as const,
};

export async function listSessions(params: ListSessionsParams): Promise<SessionResponse[]> {
  return api.get<SessionResponse[]>('/v1/calendar/sessions', {
    query: {
      from: params.from,
      to: params.to,
      teacher_id: params.teacher_id || undefined,
      room_id: params.room_id || undefined,
      student_id: params.student_id || undefined,
      status: params.status || undefined,
      subject_id: params.subject_id || undefined,
      branch_id: params.branch_id || undefined,
    },
  });
}

export async function getSession(id: string): Promise<SessionResponse> {
  return api.get<SessionResponse>(`/v1/calendar/sessions/${id}`);
}

export async function updateSession(id: string, body: SessionUpdate): Promise<SessionResponse> {
  return api.patch<SessionResponse>(`/v1/calendar/sessions/${id}`, body);
}

export async function cancelSession(
  id: string,
  body: SessionCancelRequest,
): Promise<SessionResponse> {
  return api.post<SessionResponse>(`/v1/calendar/sessions/${id}/cancel`, body);
}

/** Single session — the session detail page's primary read (11E.7). */
export function useSession(id: string | undefined) {
  return useQuery({
    queryKey: sessionKeys.detail(id ?? '__none__'),
    queryFn: () => getSession(id as string),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useSessions(params: ListSessionsParams) {
  return useQuery({
    queryKey: sessionKeys.list(params),
    queryFn: () => listSessions(params),
    staleTime: 15_000,
  });
}

interface OptimisticContext {
  previous: SessionResponse[] | undefined;
}

/**
 * ``listParams`` (optional) identifies the cache slice to patch optimistically.
 * Omitting it disables the optimistic behaviour but still invalidates on
 * success — fine for callers that don't render a list.
 */
export function useUpdateSession(listParams?: ListSessionsParams) {
  const qc = useQueryClient();
  const queryKey = listParams ? sessionKeys.list(listParams) : null;
  return useMutation<
    SessionResponse,
    unknown,
    { id: string; body: SessionUpdate & { starts_at?: string; ends_at?: string } },
    OptimisticContext
  >({
    mutationFn: ({ id, body }) => updateSession(id, body),
    onMutate: async ({ id, body }) => {
      if (!queryKey) return { previous: undefined };
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<SessionResponse[]>(queryKey);
      if (previous) {
        qc.setQueryData<SessionResponse[]>(
          queryKey,
          previous.map((s) =>
            s.id === id
              ? {
                  ...s,
                  ...(body.starts_at ? { starts_at: body.starts_at } : {}),
                  ...(body.ends_at ? { ends_at: body.ends_at } : {}),
                  ...(body.teacher_id ? { teacher_id: body.teacher_id } : {}),
                  ...('room_id' in body ? { room_id: body.room_id ?? null } : {}),
                }
              : s,
          ),
        );
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      // Rollback to the snapshot captured in onMutate.
      if (queryKey && ctx?.previous !== undefined) {
        qc.setQueryData(queryKey, ctx.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: sessionKeys.all });
    },
  });
}

export function useCancelSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SessionCancelRequest }) =>
      cancelSession(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKeys.all }),
  });
}

// ---------------------------------------------------------------------------
// 11E.7 — session-page actions
// ---------------------------------------------------------------------------

export async function deleteSession(id: string): Promise<void> {
  return api.delete<void>(`/v1/calendar/sessions/${id}`);
}

export async function duplicateSession(
  id: string,
  body: SessionDuplicateRequest,
): Promise<SessionResponse> {
  return api.post<SessionResponse>(`/v1/calendar/sessions/${id}/duplicate`, body);
}

export async function editRepeats(
  id: string,
  body: SeriesEditRequest,
): Promise<SeriesEditResult> {
  return api.post<SeriesEditResult>(`/v1/calendar/sessions/${id}/edit-repeats`, body);
}

export function useDeleteSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSession(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKeys.all }),
  });
}

export function useDuplicateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SessionDuplicateRequest }) =>
      duplicateSession(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKeys.all }),
  });
}

export function useEditRepeats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SeriesEditRequest }) => editRepeats(id, body),
    onSuccess: () => {
      // A series edit re-materialises sessions AND may split the class, so
      // both caches are stale — not just the one the user is looking at.
      qc.invalidateQueries({ queryKey: sessionKeys.all });
      qc.invalidateQueries({ queryKey: ['calendar', 'classes'] });
    },
  });
}

